import api from "./client";


export async function iniciarAuditoria(id: number) {
	try {
		const res = await api.post(`/auditoria/${id}/iniciar`);
		return res.data;
	} catch (err: any) {
		console.error("Erro ao iniciar auditoria:", err);
		throw new Error(
			err?.response?.data?.message || "Erro ao iniciar auditoria.",
		);
	}
}

export async function criarAuditoria(data: {
	usuario_id: number;
	localizacao_id: number;
	ocorrencias: { ocorrencia_id: number }[]; // ✅ array de objetos
}) {
	try {
		const res = await api.post(`/auditoria`, data);
		return res.data;
	} catch (err: any) {
		console.error("❌ Erro ao criar auditoria:", err?.response?.data || err);
		throw new Error(err?.response?.data?.message || "Erro ao criar auditoria.");
	}
}


// 🔓 Abrir localização
export async function abrirLocalizacao(
	ean: string,
	usuario_id: number,
	tipo: string = "entrada",
): Promise<void> {
	try {
		await api.get(`/movimentacao/abrir-localizacao/${ean}`, {
			params: { usuario_id, tipo },
		});
	} catch (err: any) {
		console.error("Erro ao abrir localização:", err);
		throw new Error(
			err?.response?.data?.message || "Falha ao abrir localização.",
		);
	}
}


// 🔒 Fechar localização
export async function fecharLocalizacao(
	ean: string,
	usuario_id: number,
): Promise<void> {
	try {
		await api.get(`/movimentacao/fechar-localizacao/${ean}`, {
			params: { usuario_id },
		});
	} catch (err: any) {
		console.error("Erro ao fechar localização:", err);
		throw new Error(
			err?.response?.data?.message || "Falha ao fechar localização.",
		);
	}
}


// 🗺️ Mapeamento de produtos em localização
export async function realizarMapeamento(
	localizacao_id: number,
	produtos_ids: number[],
	usuario_id: number,
): Promise<{ message: string; status: string }> {
	try {
		const response = await api.post("/produto-estoque/mapeamento", {
			localizacao_id,
			produtos_ids,
			usuario_id,
		});
		return response.data;
	} catch (err: any) {
		console.error("Erro ao realizar mapeamento:", err);
		throw new Error(
			err?.response?.data?.message || "Falha ao realizar mapeamento.",
		);
	}
}


// Função para buscar auditoria por ID (apenas uma vez!)
export async function buscarAuditoriaPorId(auditoriaId: number) {
	try {
		const response = await api.get(`/auditoria/${auditoriaId}`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar auditoria por ID:", error.message);
		throw new Error("Falha ao carregar a auditoria.");
	}
}


// Função para buscar produtos (ocorrências) de uma auditoria (apenas uma vez!)
export async function buscarProdutosAuditoria(auditoriaId: number) {
	try {
		const response = await api.get(
			`/auditoria/${auditoriaId}/listar-ocorrencias`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao cancelar auditoria:", error.message);
		throw new Error("Falha ao cancelar a auditoria.");
	}
}


// Função para cancelar auditoria
export async function cancelarAuditoria(auditoriaId: number) {
	try {
		const response = await api.post(`/auditoria/${auditoriaId}/cancelar`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao cancelar auditoria:", error.message);
		throw new Error(
			error?.response?.data?.message || "Falha ao cancelar a auditoria.",
		);
	}
}


// ==================== FUNÇÕES DE BALANÇO ====================

export interface Balanco {
	balanco_id: number;
	data_criacao: string;
	data_inicio: string | null;
	data_conclusao: string | null;
	data_ajuste: string | null;
	status: "pendente" | "em andamento" | "concluido" | "ajustado" | "cancelado";
	armazem: {
		armazem_id: number;
		nome: string;
	};
	usuario_criador: {
		usuario_id: number;
		responsavel: string;
		usuario: string;
	};
	balanco_usuarios?: Array<{
		balanco_usuario_id: number;
		usuario: {
			usuario_id: number;
			responsavel: string;
			usuario: string;
		};
	}>;
	balanco_localizacoes?: Array<{
		balanco_localizacao_id: number;
		localizacao: {
			localizacao_id: number;
			nome: string;
			ean: string;
			tipo: {
				tipo_localizacao_id: number;
				tipo: string;
			};
		};
		status: "pendente" | "validando" | "em andamento" | "concluida";
		data_conclusao?: string | null;
		data_ajuste?: string | null;
		balanco_usuario: {
			usuario: {
				usuario_id: number;
				responsavel: string;
			};
		};
		itens_balanco?: Array<{
			item_balanco_id: number;
			produto: {
				produto_id: number;
				descricao: string;
				sku: string;
				ean: string;
			};
			quantidade_sistema: number;
			quantidade_fisica: number;
			diferenca: number;
			quantidade_primeira_conferencia?: number | null;
			quantidade_segunda_conferencia?: number | null;
			quantidade_terceira_conferencia?: number | null;
			data_primeira_conferencia?: string | null;
			data_segunda_conferencia?: string | null;
			data_terceira_conferencia?: string | null;
		}>;
	}>;
}


export interface BalancoStatsResumo {
	totalLocalizacoes: number;
	concluidas: number;
	pendentes: number;
	emAndamento: number;
	totalItens: number;
	totalDivergencias: number;
	totalProdutosComDivergencia: number;
	percentualConcluido: number;
	localizacoesPrecisamReajustar: number;
	dadosGraficoStatus: Array<{ name: string; value: number; color: string }>;
	dadosGraficoDivergencias: Array<{
		chave: string;
		nome: string;
		nomeCompleto: string;
		tipo: string;
		tipoCurto: string;
		divergencias: number;
		itens: number;
	}>;
	usuariosResponsaveis: Array<{
		usuario_id: number;
		responsavel: string;
		concluidas: number;
		total: number;
	}>;
}


export interface BalancoResumoResponse {
	balanco: Balanco;
	stats: BalancoStatsResumo;
}


export interface CreateBalancoDto {
	armazem_id: number;
	usuario_criador_id: number;
	usuario_ids: number[];
}


/**
 * Criar novo balanço (já distribui localizações entre usuários)
 */
export async function criarBalanco(dto: {
	armazem_id: number;
	usuario_criador_id: number;
	usuario_ids: number[];
}): Promise<Balanco> {
	try {
		const response = await api.post("/balanco", dto);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar balanço:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao criar balanço.");
	}
}


/**
 * Listar todos os balanços
 */
export async function listarBalanços(): Promise<Balanco[]> {
	try {
		const response = await api.get("/balanco");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar balanços:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao listar balanços.");
	}
}


/**
 * Buscar balanço por ID
 */
/**
 * Realoca tarefas pendentes de um balanço entre usuários
 */
export async function realocarTarefasBalanco(
	balanco_id: number,
	usuario_origem_id: number | undefined,
	usuario_destino_ids: number[],
): Promise<Balanco> {
	try {
		const response = await api.post(`/balanco/${balanco_id}/realocar-tarefas`, {
			balanco_id,
			usuario_origem_id,
			usuario_destino_ids,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao realocar tarefas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao realocar tarefas.");
	}
}


export async function gerarRelatorioExcelBalanco(
	balanco_id: number,
): Promise<void> {
	try {
		const response = await api.get(`/balanco/${balanco_id}/relatorio-excel`, {
			responseType: "blob",
		});

		// Criar link para download
		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", `relatorio-balanco-${balanco_id}.xlsx`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	} catch (error: any) {
		console.error("Erro ao gerar relatório Excel:", error);
		throw new Error(
			error.response?.data?.message || "Falha ao gerar relatório Excel.",
		);
	}
}


/**
 * Gera relatório Excel de produtos ajustados no balanço (agrupado por produto)
 * Só funciona quando todas as localizações estiverem concluídas
 */
export async function gerarRelatorioExcelProdutosBalanco(
	balanco_id: number,
): Promise<void> {
	try {
		const response = await api.get(
			`/balanco/${balanco_id}/relatorio-excel-produtos`,
			{
				responseType: "blob",
			},
		);

		// Criar link para download
		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute(
			"download",
			`relatorio-produtos-balanco-${balanco_id}.xlsx`,
		);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	} catch (error: any) {
		console.error("Erro ao gerar relatório Excel de produtos:", error);
		throw new Error(
			error.response?.data?.message ||
				"Falha ao gerar relatório Excel de produtos.",
		);
	}
}


export async function buscarTransferenciasLocalizacao(
	balanco_id: number,
): Promise<any> {
	try {
		const response = await api.get(
			`/balanco/${balanco_id}/transferencias-localizacao`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar transferências de localização:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar transferências de localização.");
	}
}


export async function buscarBalanco(
	balanco_id: number,
): Promise<BalancoResumoResponse> {
	try {
		// ✅ NOVO FLUXO (PERFORMANCE): carregar resumo leve (sem todas as localizações)
		// A lista de localizações é buscada paginada via buscarLocalizacoesBalanco(...)
		const response = await api.get(`/balanco/${balanco_id}/resumo?all=true`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar balanço:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar balanço.");
	}
}


/**
 * Buscar localizações do balanço paginadas (WEB)
 */
export async function buscarLocalizacoesBalanco(
	balanco_id: number,
	params: {
		page?: number;
		limit?: number;
		status?: string;
		tipo?: string | number;
		q?: string;
	} = {},
): Promise<{
	data: any[];
	meta: { page: number; limit: number; total: number; totalPages: number };
}> {
	try {
		const qs = new URLSearchParams();
		qs.set("all", "true");
		if (params.page) qs.set("page", String(params.page));
		if (params.limit) qs.set("limit", String(params.limit));
		if (params.status) qs.set("status", String(params.status));
		if (
			params.tipo !== undefined &&
			params.tipo !== null &&
			String(params.tipo).trim() !== ""
		)
			qs.set("tipo", String(params.tipo));
		if (params.q) qs.set("q", String(params.q));

		const response = await api.get(
			`/balanco/${balanco_id}/localizacoes?${qs.toString()}`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar localizações do balanço:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar localizações do balanço.");
	}
}


/**
 * Finalizar balanço do armazém (ajusta saldo)
 */
export async function finalizarBalancoArmazem(
	balanco_id: number,
	usuario_id: number,
): Promise<any> {
	try {
		const response = await api.post(`/balanco/${balanco_id}/ajustar-saldo`, {
			usuario_id,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao finalizar balanço:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao finalizar balanço.");
	}
}


/**
 * Buscar itens de uma localização específica do balanço
 */
export async function buscarItensLocalizacao(
	balanco_id: number,
	balanco_localizacao_id: number,
): Promise<any> {
	try {
		const response = await api.get(
			`/balanco/${balanco_id}/localizacoes/${balanco_localizacao_id}`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar itens da localização:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar itens da localização.");
	}
}


/**
 * Ajustar saldo de uma localização específica
 */
export async function ajustarSaldoLocalizacao(
	balanco_id: number,
	balanco_localizacao_id: number,
): Promise<any> {
	try {
		const response = await api.post(
			`/balanco/${balanco_id}/localizacoes/${balanco_localizacao_id}/ajustar-saldo`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao ajustar saldo da localização:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao ajustar saldo da localização.");
	}
}


/**
 * Marca uma localização como não encontrada
 */
export async function marcarLocalizacaoNaoEncontrada(
	balanco_id: number,
	balanco_localizacao_id: number,
): Promise<any> {
	try {
		const response = await api.post(
			`/balanco/${balanco_id}/localizacoes/${balanco_localizacao_id}/marcar-nao-encontrada`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao marcar localização como não encontrada:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao marcar localização como não encontrada.");
	}
}


/**
 * Zera e conclui uma localização já marcada como não encontrada (sem depender do limite de rodízio)
 * Usado tipicamente na web/admin.
 */
export async function zerarLocalizacaoNaoEncontrada(
	balanco_id: number,
	balanco_localizacao_id: number,
): Promise<any> {
	try {
		const response = await api.post(
			`/balanco/${balanco_id}/localizacoes/${balanco_localizacao_id}/zerar-nao-encontrada`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao zerar localização não encontrada:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao zerar localização não encontrada.");
	}
}


/**
 * Reinicia a procura de uma localização não encontrada (após 2x)
 */
export async function reiniciarProcuraLocalizacao(
	balanco_id: number,
	balanco_localizacao_id: number,
	usuario_id_realocar: number,
): Promise<any> {
	try {
		const response = await api.post(
			`/balanco/${balanco_id}/localizacoes/${balanco_localizacao_id}/reiniciar-procura`,
			{ usuario_id_realocar },
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao reiniciar procura da localização:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao reiniciar procura da localização.");
	}
}

